import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2, ArrowLeft, GripVertical, Eye, X, Tag } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '@/components/loading';
import { useProfile } from '@/lib/providers';
import { AdminRole, Content, ContentWithConfig, ContentConfig } from '@/types/custom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { normalizeImageUrl } from '@/lib/utils';
import { RichTextViewer } from '@/components/ui/rich-text-viewer';
import ArticleEdit from '@/components/article/article-edit';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTipsTopics, TipsTopic } from '@/lib/hooks/use-tips-topics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type ScopeType = 'global' | 'sector' | 'organization' | 'team' | 'user';

interface ScopeEntity {
  id: string;
  name: string;
}

const SCOPE_LABELS: Record<ScopeType, string> = {
  global: 'Iedereen',
  sector: 'Sector',
  organization: 'Organisatie',
  team: 'Team',
  user: 'Gebruiker',
};

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '...';
}

function SortableArticleCard({
  item,
  isAdmin,
  onOpen,
  onEdit,
  onDelete,
}: {
  item: ContentWithConfig;
  isAdmin: boolean;
  onOpen: (id: string) => void;
  onEdit: (article: Content) => void;
  onDelete: (article: Content) => void;
}) {
  const article = item.content;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: article.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const preview = article.description
    || (article.body ? truncateText(stripHtml(article.body), 120) : '');

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className="overflow-hidden flex flex-col cursor-pointer hover:shadow-lg transition-shadow h-full"
        onClick={() => onOpen(article.id)}
      >
        {article.image_url && (
          <img
            src={normalizeImageUrl(article.image_url)}
            alt={article.title}
            className="w-full h-48 object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
        <div className="p-5 flex flex-col flex-grow">
          <div className="flex items-start justify-between gap-2">
            {isAdmin && (
              <button
                className="p-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex-shrink-0 touch-none"
                {...attributes}
                {...listeners}
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-4 w-4" />
              </button>
            )}
            <h2 className="text-lg font-semibold leading-tight flex-grow">
              {article.title}
            </h2>
            {isAdmin && (
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(article); }}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(article); }}
                  className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>
            )}
          </div>

          {preview && (
            <p className="mt-3 text-sm text-muted-foreground line-clamp-3">
              {preview}
            </p>
          )}

          <span className="mt-3 text-sm font-medium text-green-600">
            Lees meer
          </span>
        </div>
      </Card>
    </div>
  );
}

/**
 * Tips & Artikelen page — card grid with detail view, View As, and drag-to-reorder for admin.
 */
export default function TipsPage() {
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;
  const { toast } = useToast();

  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Admin state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Content | null>(null);

  // Tips topics state
  const { topics: tipsTopics, loading: loadingTopics, createTopic, updateTopic, deleteTopic, getTopicsForContent, setTopicsForContent } = useTipsTopics();
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<TipsTopic | null>(null);
  const [topicName, setTopicName] = useState('');
  const [topicSlug, setTopicSlug] = useState('');

  // Per-article topic associations (content_id -> topic_id[])
  const [articleTopics, setArticleTopics] = useState<Record<string, string[]>>({});
  // Topics for the currently selected article (detail view)
  const [selectedArticleTopicIds, setSelectedArticleTopicIds] = useState<string[]>([]);

  // View As state (admin only)
  const [scopeType, setScopeType] = useState<ScopeType>('global');
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [sectors, setSectors] = useState<ScopeEntity[]>([]);
  const [organizations, setOrganizations] = useState<ScopeEntity[]>([]);
  const [teams, setTeams] = useState<ScopeEntity[]>([]);
  const [users, setUsers] = useState<ScopeEntity[]>([]);

  const [orderedArticles, setOrderedArticles] = useState<ContentWithConfig[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Fetch entity lists for admin View As
  useEffect(() => {
    if (!isAdmin) return;
    const fetchEntities = async () => {
      const [sectorRes, orgRes, teamRes, userRes] = await Promise.all([
        supabase.from('sector').select('id, name').order('name'),
        supabase.from('organization').select('id, name').order('name'),
        supabase.from('team').select('id, name').order('name'),
        supabase.from('profiles').select('id, naam').order('naam'),
      ]);
      if (sectorRes.data) setSectors(sectorRes.data.map((s) => ({ id: s.id, name: s.name })));
      if (orgRes.data) setOrganizations(orgRes.data.map((o) => ({ id: o.id, name: o.name })));
      if (teamRes.data) setTeams(teamRes.data.map((t) => ({ id: t.id, name: t.name })));
      if (userRes.data)
        setUsers(
          userRes.data
            .filter((u) => u.naam)
            .map((u) => ({ id: u.id, name: u.naam! })),
        );
    };
    fetchEntities();
  }, [isAdmin]);

  useEffect(() => {
    setScopeId(null);
  }, [scopeType]);

  const getEntityList = (): ScopeEntity[] => {
    switch (scopeType) {
      case 'sector': return sectors;
      case 'organization': return organizations;
      case 'team': return teams;
      case 'user': return users;
      default: return [];
    }
  };

  const fetchArticles = async () => {
    setLoading(true);

    if (isAdmin) {
      if (scopeType !== 'global' && !scopeId) {
        setOrderedArticles([]);
        setLoading(false);
        return;
      }

      // Build scopes that the selected entity would see
      const scopes: string[] = [
        'and(scope_type.eq.global,scope_id.is.null)',
      ];

      if (scopeType === 'user' && scopeId) {
        scopes.push(`and(scope_type.eq.user,scope_id.eq.${scopeId})`);
        const { data: memberships } = await supabase
          .from('team_members')
          .select('team:team(id, organization_id, organization:organization(id, sector_id))')
          .eq('profile_id', scopeId);
        for (const m of memberships ?? []) {
          const t = m.team as any;
          if (t) {
            scopes.push(`and(scope_type.eq.team,scope_id.eq.${t.id})`);
            scopes.push(`and(scope_type.eq.organization,scope_id.eq.${t.organization_id})`);
            if (t.organization?.sector_id) {
              scopes.push(`and(scope_type.eq.sector,scope_id.eq.${t.organization.sector_id})`);
            }
          }
        }
      } else if (scopeType === 'team' && scopeId) {
        scopes.push(`and(scope_type.eq.team,scope_id.eq.${scopeId})`);
        const { data: teamData } = await supabase
          .from('team')
          .select('organization_id, organization:organization(sector_id)')
          .eq('id', scopeId)
          .single();
        if (teamData) {
          scopes.push(`and(scope_type.eq.organization,scope_id.eq.${teamData.organization_id})`);
          const org = teamData.organization as any;
          if (org?.sector_id) {
            scopes.push(`and(scope_type.eq.sector,scope_id.eq.${org.sector_id})`);
          }
        }
      } else if (scopeType === 'organization' && scopeId) {
        scopes.push(`and(scope_type.eq.organization,scope_id.eq.${scopeId})`);
        const { data: orgData } = await supabase
          .from('organization')
          .select('sector_id')
          .eq('id', scopeId)
          .single();
        if (orgData?.sector_id) {
          scopes.push(`and(scope_type.eq.sector,scope_id.eq.${orgData.sector_id})`);
        }
      } else if (scopeType === 'sector' && scopeId) {
        scopes.push(`and(scope_type.eq.sector,scope_id.eq.${scopeId})`);
      }

      const { data, error } = await supabase
        .from('content')
        .select('*, content_configs(*), keywords(*)')
        .eq('content_type', 'article')
        .or(scopes.join(','))
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (data) {
        setOrderedArticles(data.map((item) => ({
          content: item as Content,
          configs: (item.content_configs || []) as ContentConfig[],
          keywords: item.keywords ?? [],
        })));
      }
    } else {
      // Regular user: show articles matching their scopes
      const scopes: string[] = [
        'and(scope_type.eq.global,scope_id.is.null)',
        `and(scope_type.eq.user,scope_id.eq.${profile.id})`,
      ];

      for (const team of profile?.teams ?? []) {
        scopes.push(`and(scope_type.eq.team,scope_id.eq.${team.id})`);
        scopes.push(`and(scope_type.eq.organization,scope_id.eq.${team.organization.id})`);
        scopes.push(`and(scope_type.eq.sector,scope_id.eq.${team.organization.sector.id})`);
      }

      const { data } = await supabase
        .from('content')
        .select('*, content_configs(*), keywords(*)')
        .eq('content_type', 'article')
        .or(scopes.join(','))
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (data) {
        setOrderedArticles(data.map((item) => ({
          content: item as Content,
          configs: (item.content_configs || []) as ContentConfig[],
          keywords: item.keywords ?? [],
        })));
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!loadingProfile && profile) fetchArticles();
  }, [scopeType, scopeId, loadingProfile, isAdmin]);

  // Fetch topic associations for all loaded articles
  useEffect(() => {
    if (orderedArticles.length === 0) return;
    const fetchAssociations = async () => {
      const map: Record<string, string[]> = {};
      await Promise.all(
        orderedArticles.map(async (a) => {
          map[a.content.id] = await getTopicsForContent(a.content.id);
        }),
      );
      setArticleTopics(map);
    };
    fetchAssociations();
  }, [orderedArticles]);

  // Deep link support
  useEffect(() => {
    const actiefId = searchParams.get('actief');
    if (actiefId && orderedArticles.length > 0) {
      const found = orderedArticles.find((a) => a.content.id === actiefId);
      if (found) setSelectedId(actiefId);
    }
  }, [orderedArticles]);

  const selectedArticleData = selectedId
    ? orderedArticles.find((a) => a.content.id === selectedId)
    : null;
  const selectedArticle = selectedArticleData?.content ?? null;
  const selectedArticleKeywords = selectedArticleData?.keywords ?? [];

  const openArticle = async (id: string) => {
    setSelectedId(id);
    window.history.replaceState(null, '', `${window.location.pathname}?actief=${id}`);
    const topicIds = await getTopicsForContent(id);
    setSelectedArticleTopicIds(topicIds);
  };

  const closeArticle = () => {
    setSelectedId(null);
    window.history.replaceState(null, '', window.location.pathname);
  };

  const handleDeleteArticle = async (article: Content) => {
    if (!confirm('Weet je zeker dat je dit artikel wilt verwijderen?')) return;

    try {
      await supabase.from('content').delete().eq('id', article.id);
      toast({
        title: 'Artikel verwijderd',
        description: `${article.title} is verwijderd.`,
      });
      setSelectedId(null);
      fetchArticles();
    } catch (err: any) {
      toast({
        title: 'Fout bij verwijderen artikel',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedArticles.findIndex((i) => i.content.id === active.id);
    const newIndex = orderedArticles.findIndex((i) => i.content.id === over.id);
    const reordered = arrayMove(orderedArticles, oldIndex, newIndex);
    setOrderedArticles(reordered);

    const updates = reordered.map((item, idx) => ({
      id: item.content.id,
      sort_order: idx,
    }));
    for (const u of updates) {
      await supabase.from('content').update({ sort_order: u.sort_order }).eq('id', u.id);
    }
  };

  const renderAdminButtons = (article: Content) => {
    if (!isAdmin) return null;
    return (
      <div className="flex gap-1 flex-shrink-0">
        <button
          onClick={() => {
            setEditingArticle(article);
            setDialogOpen(true);
          }}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <Pencil className="h-4 w-4 text-muted-foreground" />
        </button>
        <button
          onClick={() => handleDeleteArticle(article)}
          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950"
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </button>
      </div>
    );
  };

  // Filter articles by active topic
  const filteredArticles = activeTopicId
    ? orderedArticles.filter((a) =>
        (articleTopics[a.content.id] ?? []).includes(activeTopicId),
      )
    : orderedArticles;

  if (loadingProfile || loading) {
    return <Loading />;
  }

  // Detail view for a selected article
  if (selectedArticle) {
    return (
      <div className="min-h-screen p-8 space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={closeArticle}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-semibold">Tips & Artikelen</h1>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="overflow-hidden">
            {selectedArticle.image_url && (
              <img
                src={normalizeImageUrl(selectedArticle.image_url)}
                alt={selectedArticle.title}
                className="w-full h-64 sm:h-80 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-2xl sm:text-3xl font-bold leading-tight">
                  {selectedArticle.title}
                </h2>
                {renderAdminButtons(selectedArticle)}
              </div>

              {selectedArticle.description && (
                <p className="mt-4 text-lg text-muted-foreground">
                  {selectedArticle.description}
                </p>
              )}

              {selectedArticle.body && (
                <div className="mt-6">
                  <RichTextViewer
                    content={selectedArticle.body}
                    className="text-base"
                  />
                </div>
              )}

              {/* Keywords display */}
              {selectedArticleKeywords.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Keywords</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedArticleKeywords.map((kw) => (
                      <span
                        key={kw.id}
                        className="inline-flex items-center rounded-full border border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 px-3 py-1 text-xs hhu-outline"
                      >
                        {kw.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Topics display / admin assignment */}
              {(selectedArticleTopicIds.length > 0 || isAdmin) && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Topics</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tipsTopics.map((topic) => {
                      const isAssigned = selectedArticleTopicIds.includes(topic.id);
                      if (!isAdmin && !isAssigned) return null;
                      return (
                        <Button
                          key={topic.id}
                          variant="outline"
                          size="sm"
                          className={`rounded-full text-xs ${
                            isAssigned
                              ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300 dark:hover:bg-blue-900'
                              : 'border-muted text-muted-foreground hover:bg-muted/80'
                          } ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}
                          onClick={async () => {
                            if (!isAdmin) return;
                            const newIds = isAssigned
                              ? selectedArticleTopicIds.filter((id) => id !== topic.id)
                              : [...selectedArticleTopicIds, topic.id];
                            setSelectedArticleTopicIds(newIds);
                            try {
                              await setTopicsForContent(selectedArticle.id, newIds);
                              setArticleTopics((prev) => ({ ...prev, [selectedArticle.id]: newIds }));
                            } catch (err: any) {
                              toast({ title: 'Fout', description: err.message, variant: 'destructive' });
                              setSelectedArticleTopicIds(isAssigned
                                ? [...selectedArticleTopicIds]
                                : selectedArticleTopicIds.filter((id) => id !== topic.id));
                            }
                          }}
                        >
                          {topic.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        <ArticleEdit
          content={editingArticle ?? undefined}
          onSave={() => fetchArticles()}
          isOpen={dialogOpen}
          handleClose={() => {
            setDialogOpen(false);
            setEditingArticle(null);
          }}
        />
      </div>
    );
  }

  // Main view
  return (
    <div className="min-h-screen p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Tips & Artikelen</h1>
        {isAdmin && (
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={() => {
              setEditingArticle(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Toevoegen
          </Button>
        )}
      </div>

      {/* View As section (admin only) */}
      {isAdmin && (
        <section className="bg-card border rounded-lg px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 mr-2">
              <Eye className="h-4 w-4 text-green-600" />
              <span className="text-sm font-semibold">Bekijken als</span>
            </div>
            <div className="w-44">
              <Select
                value={scopeType}
                onValueChange={(val) => setScopeType(val as ScopeType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Iedereen</SelectItem>
                  <SelectItem value="sector">Sector</SelectItem>
                  <SelectItem value="organization">Organisatie</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="user">Gebruiker</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scopeType !== 'global' && (
              <div className="w-56">
                <Select
                  value={scopeId ?? ''}
                  onValueChange={(val) => setScopeId(val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={`Selecteer ${SCOPE_LABELS[scopeType].toLowerCase()}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {getEntityList().map((entity) => (
                      <SelectItem key={entity.id} value={entity.id}>
                        {entity.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Topic filter buttons */}
      {!loadingTopics && tipsTopics.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {tipsTopics.map((topic) => (
            <div key={topic.id} className="relative group">
              <Button
                variant="outline"
                className={`rounded-full flex-shrink-0 ${
                  activeTopicId === topic.id
                    ? 'border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300'
                    : 'border-blue-400 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950'
                }`}
                onClick={() => {
                  setActiveTopicId(activeTopicId === topic.id ? null : topic.id);
                }}
              >
                {topic.name}
              </Button>
              {isAdmin && (
                <div className="absolute -top-1 -right-1 hidden group-hover:flex gap-0.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingTopic(topic);
                      setTopicName(topic.name);
                      setTopicSlug(topic.slug);
                      setTopicDialogOpen(true);
                    }}
                    className="flex h-5 w-5 items-center justify-center rounded-full bg-white shadow text-blue-600 hover:bg-blue-50"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`'${topic.name}' verwijderen?`)) {
                        deleteTopic(topic.id).then(() =>
                          toast({ title: 'Topic verwijderd', description: `'${topic.name}' is verwijderd.` }),
                        ).catch((err) =>
                          toast({ title: 'Fout', description: err.message, variant: 'destructive' }),
                        );
                      }
                    }}
                    className="flex h-5 w-5 items-center justify-center rounded-full bg-white shadow text-red-500 hover:bg-red-50"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-dashed border-blue-400 text-blue-600 hover:bg-blue-50"
              onClick={() => {
                setEditingTopic(null);
                setTopicName('');
                setTopicSlug('');
                setTopicDialogOpen(true);
              }}
            >
              <Plus className="h-3 w-3 mr-1" />
              Topic
            </Button>
          )}
        </div>
      )}

      {filteredArticles.length === 0 && (
        <p className="text-muted-foreground">
          {isAdmin && scopeType !== 'global' && !scopeId
            ? `Selecteer een ${SCOPE_LABELS[scopeType].toLowerCase()} om artikelen te bekijken.`
            : activeTopicId
              ? 'Geen artikelen gevonden voor dit topic.'
              : 'Er zijn momenteel nog geen tips of artikelen toegevoegd.'}
        </p>
      )}

      {/* Featured first item */}
      {filteredArticles.length > 0 && (() => {
        const firstArticle = filteredArticles[0].content;
        return (
          <div className="max-w-6xl">
            <Card className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openArticle(firstArticle.id)}>
              <div className="grid grid-cols-1 md:grid-cols-2">
                {firstArticle.image_url && (
                  <img
                    src={normalizeImageUrl(firstArticle.image_url)}
                    alt={firstArticle.title}
                    className="w-full h-64 md:h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <div className="p-6 sm:p-8 flex flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <h2 className="text-2xl font-bold leading-tight">
                      {firstArticle.title}
                    </h2>
                    {renderAdminButtons(firstArticle)}
                  </div>

                  {firstArticle.description && (
                    <p className="mt-3 text-muted-foreground">
                      {firstArticle.description}
                    </p>
                  )}

                  {firstArticle.body && (
                    <div className="mt-4 flex-grow">
                      <RichTextViewer
                        content={firstArticle.body}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        );
      })()}

      {/* Rest of the articles as draggable cards */}
      {filteredArticles.length > 1 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredArticles.slice(1).map((a) => a.content.id)} strategy={verticalListSortingStrategy}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-6xl">
              {filteredArticles.slice(1).map((item) => (
                <SortableArticleCard
                  key={item.content.id}
                  item={item}
                  isAdmin={isAdmin}
                  onOpen={openArticle}
                  onEdit={(article) => {
                    setEditingArticle(article);
                    setDialogOpen(true);
                  }}
                  onDelete={handleDeleteArticle}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <ArticleEdit
        content={editingArticle ?? undefined}
        onSave={() => fetchArticles()}
        isOpen={dialogOpen}
        handleClose={() => {
          setDialogOpen(false);
          setEditingArticle(null);
        }}
      />

      {/* Topic create/edit dialog (admin) */}
      <Dialog open={topicDialogOpen} onOpenChange={setTopicDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTopic ? 'Topic bewerken' : 'Nieuw topic'}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                if (editingTopic) {
                  await updateTopic(editingTopic.id, topicName, topicSlug);
                  toast({ title: 'Topic bijgewerkt' });
                } else {
                  await createTopic(topicName, topicSlug);
                  toast({ title: 'Topic aangemaakt' });
                }
                setTopicDialogOpen(false);
              } catch (err: any) {
                toast({ title: 'Fout', description: err.message, variant: 'destructive' });
              }
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="topic-name">Naam</Label>
              <Input
                id="topic-name"
                value={topicName}
                onChange={(e) => {
                  setTopicName(e.target.value);
                  if (!editingTopic) {
                    setTopicSlug(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/(^-|-$)/g, ''),
                    );
                  }
                }}
                placeholder="bijv. Energie"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="topic-slug">Slug (URL)</Label>
              <Input
                id="topic-slug"
                value={topicSlug}
                onChange={(e) => setTopicSlug(e.target.value)}
                placeholder="bijv. energie"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setTopicDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingTopic ? 'Opslaan' : 'Aanmaken'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
